import * as React from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { RichTextField } from "@/components/ui/rich-text-field"

// Comment composer used at the bottom of any activity feed. Domain-agnostic —
// pass the events POST endpoint and we'll route the comment there. Optionally
// pass `tab` to tag the comment as originating from a specific phase/tab; the
// server stores it on event metadata so phase-scoped feeds can filter.
export function ActivityComposer({
  eventsPath,
  tab,
}: {
  eventsPath: string
  tab?: string
}) {
  const [draft, setDraft] = React.useState("")
  const [resetKey, setResetKey] = React.useState(0)
  const [posting, setPosting] = React.useState(false)

  // Refs so the keydown handler always reads the latest draft / posting state
  // without having to rebind on every keystroke.
  const draftRef = React.useRef(draft)
  draftRef.current = draft
  const postingRef = React.useRef(posting)
  postingRef.current = posting

  const post = React.useCallback(() => {
    const body = draftRef.current.trim()
    if (body.length === 0 || postingRef.current) return
    setPosting(true)
    const payload: Record<string, string> = { body }
    if (tab) payload.tab = tab
    router.post(
      eventsPath,
      payload,
      {
        preserveScroll: true,
        onFinish: () => {
          setPosting(false)
          setDraft("")
          setResetKey((n) => n + 1)
        },
      },
    )
  }, [eventsPath, tab])

  // Cmd+Enter (mac) / Ctrl+Enter posts the comment. We attach this in the
  // capture phase so the keystroke fires before Milkdown's own keymap can
  // swallow it (Milkdown treats plain Enter as a newline, which is the
  // desired multiline behavior — only the modified combo posts).
  const onKeyDownCapture: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      e.stopPropagation()
      post()
    }
  }

  return (
    <div>
      <div
        className="rounded-md border border-hairline bg-surface p-3"
        onKeyDownCapture={onKeyDownCapture}
      >
        <RichTextField
          key={resetKey}
          defaultValue=""
          onChange={setDraft}
          placeholder="Write a comment — markdown supported. ⌘↵ to post."
        />
        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={posting || draft.trim().length === 0}
            onClick={post}
          >
            {posting ? "Posting…" : "Post"}
          </Button>
        </div>
      </div>
    </div>
  )
}
